import React, { useRef } from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExport from 'highcharts/modules/export-data';
import HighchartsExporting from 'highcharts/modules/exporting';

import { shallowEqual, useSelector } from 'react-redux';
import { Skeleton } from '../../../../../../../components';
import { optimizationPricesResultSelector } from '../../../../../../../reducers/OtimizacaoPrecos';
import { COLORS } from '../ChartVariation';
import styles from './histogramChart.module.scss';

HighchartsMore(Highcharts);
HighchartsExporting(Highcharts);
HighchartsExport(Highcharts);

interface HistogramChartProps {
  isLoading?: boolean;
}

export const HistogramChart = ({ isLoading }: HistogramChartProps) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const ref = useRef(null);

  const { tabs } = useSelector(optimizationPricesResultSelector, shallowEqual);
  const { charts } = tabs.history;

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      spacingBottom: 30,
    },
    exporting: {
      enabled: false,
    },
    title: {
      text: '',
    },
    yAxis: {
      title: {
        text: '',
        style: {
          fontWeight: 'bold',
          color: '#323232',
          fontSize: '8px',
          textTransform: 'uppercase',
        },
      },
    },

    xAxis: {
      title: {
        text: '',
      },
      categories: charts?.categories,
    },
    colors: COLORS,
    tooltip: {
      useHTML: true,
      shared: true,
      backgroundColor: '#fff',
      borderColor: '#999',
      borderRadius: 8,
      borderWidth: 1,
      style: {
        fontSize: '13px',
        color: '#555',
        fontFamily: 'sans-serif',
      },
      formatter: function () {
        const [sugeridos, aplicados] = this.points as any;
        const date = this.x;

        return `
                <div style="padding: 8px;">
                  <div style="text-align: left; margin-bottom: 8px; color: #888;">
                    ${date}
                  </div>

                  <div style="display: flex; align-items: center; margin-bottom: 8px;">
                    <span style="width: 8px; height: 8px; background: ${sugeridos.color}; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>
                    Qtd preços sugeridos: <b>${sugeridos.y}</b>
                  </div>

                  <div style="display: flex; align-items: center;">
                    <span style="width: 8px; height: 8px; background: ${aplicados.color}; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>
                    Qtd preços aplicados: <b>${aplicados.y}</b>
                  </div>
                </div>
              `;
      },
    },
    legend: {
      symbolRadius: 2,
    },
    series: charts?.series.map(item => {
      return {
        type: 'column',
        name: item.name,
        borderWidth: 0,
        data: item.data.map(point => (typeof point === 'object' && point !== null ? { ...(point as any) } : point)),
      };
    }),
  };

  if (isLoading) {
    return (
      <div className={styles.container} style={{ height: '400px' }}>
        <Skeleton width={'100%'} height={'400px'} />
      </div>
    );
  }

  if ((charts?.categories.length === 0 || charts?.series.length === 0) && !isLoading) {
    return (
      <div className={styles.container} style={{ height: '400px' }}>
        <div className={styles['no-data']}>
          <p className={styles['no-data-title']}>Nenhum dado encontrado</p>
          <p className={styles['no-data-subtitle']}>Não há dados para exibir neste gráfico</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} ref={ref} style={{ height: '400px' }}>
      <div className={styles['title-container']}>
        <span className={styles.title}>Histograma de preço</span>
        <span className={styles.subtitle}>Gráfico comparativo de quantidade de preços sugeridos vs preços aplicados</span>
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
        containerProps={{ style: { width: '99%', height: '100%', margin: '0 auto' } }}
      />
    </div>
  );
};
